<template>
  <transition name="modal">
    <div class="modal-mask" :class="{ 'is-open': show }" v-show="show" @mousedown="onMaskMouseDown" @click="clickAwayClose">
      <div
        ref="k-modal"
        role="dialog"
        aria-labelledby="modal-header-slot"
        aria-modal="true"
        class="modal-container"
        tabindex="-1"
      >
        <div class="modal-header">
          <div id="modal-header-slot">
            <slot name="header"></slot>
          </div>
          <button type="button" class="modal-close-button btn" @click="close" role="button" aria-label="Close modal" title="Close modal"><i class="fas fa-times" aria-hidden="true"></i></button>
        </div>
        <div class="modal-body">
          <slot name="body"></slot>
        </div>
        <div class="modal-footer text-right">
          <slot name="footer">
          <button class="modal-default-button btn btn-outlined" @click="close">
            Close
          </button>
          </slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background-color: rgb(0 0 0 / 0.5);
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding-block: 16px;
  padding-inline: 16px;
}

.modal-container {
  display: flex;
  flex-direction: column;
  min-width: 50%;
  max-width: 95vw;
  max-height: calc(100vh - 32px);
  overflow: hidden;
  overscroll-behavior: contain;
  margin: 0 auto;
  background-color: var(--kate-panel-alt);
  border-radius: 5px;
  transition: all 0.3s ease;
  position: relative;
  color: var(--kate-type-light);
}

.modal-container:focus-visible {
  outline: none;
}

/* modal inside modal */
.modal-container .modal-container {
  max-width: 90vw;
}

.modal-header {
  flex-shrink: 0;
  padding: 20px 30px;
  padding-right: 48px;
  border-bottom: 2px solid var(--kate-background-alpha);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  margin: 20px 0;
  padding: 0 30px;
  overflow-y: auto;
  position: relative;
}

.text-right {
  text-align: right;
}

.modal-close-button {
  position: absolute;
  top: 0;
  right: 0;
  font-size: large;
  color: var(--kate-type-primary);
  background-color: transparent;
}

.modal-close-button:hover {
  color: var(--kate-type-primary);
}

.modal-footer {
  flex-shrink: 0;
  border-top: 0;
  margin-top: 10px;
  padding: 10px 30px 15px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
}

.modal-footer:has(.modal-footer-left):has(.modal-footer-right) {
  justify-content: space-between;
}

.modal-footer::after,
.modal-footer::before {
  content: "";
  display: none;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  transform: scale(1.1);
}

@media (min-width: 768px) and (max-width: 900px) {
  .modal-container {
    width: 70%;
  }
}

@media only screen and (max-width: 767px) {
  .modal-header,
  .modal-body {
    padding: 15px 30px;
  }

  .modal-body {
    margin: 0;
  }

  .modal-container {
    width: 90%;
  }
}

</style>

<script>
import { createFocusTrap, getFocusableElements } from '@utils/trap-focus';

export default {
  props: {
    show: {
      type: Boolean,
    },
    closeOnClickAway: {
      type: Boolean,
      default: true,
    },
  },

  mounted() {
    document.addEventListener('keydown', this.keydownHandler);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.keydownHandler);
    this.endFocusTrap();
  },

  data() {
    return {
      lastFocused: null,
      focusTrap: null,
      pointerDownOnMask: false,
    };
  },

  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.lastFocused = document.activeElement;
          this.$nextTick(() => {
            const focusable = getFocusableElements(this.$refs['k-modal']);
            const firstFocusable = focusable.find(el => !el.classList.contains('modal-close-button'));
            const elementToFocus = firstFocusable || focusable[0];
            elementToFocus?.focus();
            this.focusTrap = createFocusTrap(this.$refs['k-modal']);
          });
        } else {
          this.endFocusTrap();
        }
      },
    },
  },

  methods: {
    endFocusTrap() {
      this.focusTrap?.remove();
      this.lastFocused?.focus();
      this.lastFocused = null;
    },
    close() {
      this.$emit('close');
    },
    keydownHandler(e) {
      // Close on esc keydown
      if (this.show && e.keyCode === 27) {
        this.close();
      }
    },

    onMaskMouseDown(e) {
      this.pointerDownOnMask = e.target === e.currentTarget;
    },

    clickAwayClose(e) {
      if (
        this.closeOnClickAway
        && this.pointerDownOnMask
        && e.target === e.currentTarget
      ) {
        this.close();
      }
      this.pointerDownOnMask = false;
    },
  },
};
</script>
